<template>
  <div class="bbe-sidebar">
    <div class="bbe-sidebar-header">
      <label class="bbe-sidebar-header-search">
        <transition-group tag="div" name="bbe-fade-options" class="bbe-sidebar-header-search__icon-wrapper">
          <i class="email-icon-search bbe-sidebar-header-search__icon"
             :key="'name'"
             v-if="!searchByHashtag"/>
          <i class="email-icon-hashtag bbe-sidebar-header-search__icon bbe-sidebar-header-search__icon_tag"
             :key="'hashtag'"
             v-else/>
        </transition-group>
        <input class="bbe-input bbe-input_search bbe-sidebar-header-search__input"
               type="text"
               ref="bbeInputSearch"
               @input="inputSearch"
               :placeholder="searchByHashtag ? 'Hashtag' : 'Search'">
        <span class="bbe-sidebar-header-search-select"
              v-if="activeTab !== 'TEMPLATES'"
              @click="openSelectSearch = !openSelectSearch">
          <span class="bbe-sidebar-header-search-select__title">{{searchBySelect}}</span>
          <i class="email-icon-arrow bbe-sidebar-header-search-select__icon"
             :class="{'bbe-sidebar-header-search-select__icon_active': openSelectSearch}"/>
          <transition name="bbe-bounce">
            <span class="bbe-sidebar-header-search-options"
                  @mouseleave="openSelectSearch = false"
                  v-if="openSelectSearch">
              <span class="bbe-sidebar-header-search-options__item"
                    v-for="option in searchOptions"
                    :key="option"
                    @click="setTypeSearch(option)">
                {{option}}
              </span>
            </span>
          </transition>
        </span>
        <span class="bbe-sidebar-header-search-select"
              v-if="activeTab === 'TEMPLATES'"
              @click.stop.prevent="openSelectSearch = !openSelectSearch">
          <span class="bbe-sidebar-header-search-select__title">filter</span>
          <i class="email-icon-settings bbe-sidebar-header-search-select__icon_settings"/>
           <transition name="bbe-bounce">
             <SearchFilter v-if="openSelectSearch" @close="openSelectSearch = !openSelectSearch"/>
           </transition>
        </span>
      </label>
      <transition name="bbe-fade-options">
        <VuePerfectScrollbar class="bbe-sidebar-header-tags"
                             :class="{
                               'bbe-sidebar-header-tags_start': scrollInStart
                             }"
                             @ps-scroll-x="setScrollPosition"
                             v-dragscroll
                             v-if="searchByHashtag">
          <div class="bbe-sidebar-header-tags__tag"
               v-for="tag in fullTagList"
               :key="tag.id"
               @click="setFilterByTag(tag)">
            #{{tag.name}}
          </div>
          <div class="bbe-sidebar-header-tags__padding"/>
        </VuePerfectScrollbar>
      </transition>
      <div class="bbe-sidebar-header-tabs" v-dragscroll>
        <div class="bbe-sidebar-header-tabs-item"
             @click="clickToTab(tab)"
             v-for="tab in tabs" :key="tab.id"
             :class="{
                 'bbe-sidebar-header-tabs-item_active': activeTab === tab.id
               }">
          <i class="bbe-sidebar-header-tabs-item__icon"
             :class="'email-icon-' + tab.icon"
             @mouseleave="showTabTitle = false"
             @mouseenter="showTabTitle = tab.id"/>
          <span class="bbe-sidebar-header-tabs-item__count" v-if="tab.threadsUnread">
            {{tab.threadsUnread}}
          </span>
          <transition name="bbe-bounce">
            <span class="bbe-sidebar-header-tabs-item__title" v-if="showTabTitle === tab.id">
              {{tab.title}}
            </span>
          </transition>
        </div>
      </div>
    </div>
    <div class="bbe-sidebar-content">
      <template v-if="activeTab !== 'TEMPLATES'">
        <MailsList
          :type="activeTab"
          :filterByTag="filterByTag"
          ref="bbeMailsList"
          @updateCounter="setCounters"
          @resetActiveTab="resetActiveTab"
        />
      </template>
      <template v-else>
        <TemplatesSidebar/>
      </template>
    </div>
  </div>
</template>

<script>
import MailsList from '@/components/Email/EmailList/EmailList'
import {sidebarTabs, labelTypes} from '@/constants/sidebarTabs'
import {get, findIndex, filter, find, debounce, orderBy} from 'lodash'
import TemplatesSidebar from '@/components/Templates/TemplatesSidebar/TemplatesSidebar'
import routerPush from '@/mixin/routerPush'
import authServiceAccount from '@/mixin/authServiceAccount'
import controlsGmailSendAs from '@/mixin/controlsGmailSendAs'
import tagsApi from '@/api/Tags/api'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {dragscroll} from 'vue-dragscroll'
import clearLabels from '@/mixin/clearLabels'
import checkbox from '@/components/Main/FormsElements/checkbox'
import EmailRadioInput from '@/components/Main/FormsElements/EmailRadioInput'
import SearchFilter from '../SearchFilter/SearchFilter'

export default {
  name: 'EmailSidebar',
  mixins: [routerPush, authServiceAccount, controlsGmailSendAs, clearLabels],
  directives: {
    dragscroll
  },
  components: {
    SearchFilter,
    EmailRadioInput,
    checkbox,
    TemplatesSidebar,
    MailsList,
    VuePerfectScrollbar
  },
  data () {
    return {
      showTabTitle: false,
      tabs: sidebarTabs,
      timeLastInputSearch: '',
      labelTypes: labelTypes,
      searchBySelect: 'name',
      openSelectSearch: false,
      searchOptions: ['name', 'hashtag'],
      fullTagList: [],
      filterByTag: {},
      scrollInStart: true
    }
  },
  computed: {
    isDev () {
      return process.env.NODE_ENV === 'development' || !process.env.NODE_ENV
    },
    userGmail () {
      return get(this.activeUser, 'gmail.email', '')
    },
    userEmail () {
      const props = this.isDev ? 'gmail.email' : 'email'
      return get(this.activeUser, props, '')
    },
    searchByHashtag () {
      return this.searchBySelect === 'hashtag'
    },
    configApiKeys () {
      return this.$store.getters.configApiKeys
    },
    sendAsGmail () {
      return this.configApiKeys.sendAsGmail
    },
    activeUser: {
      get: function () {
        return this.$store.getters.userInfo || this.$store.getters.activeUser
      },
      set: function (data) {
        if (this.$store.getters.userInfo) {
          this.$store.dispatch('setUserInfo', data)
        } else {
          this.$store.dispatch('setActiveUser', data)
        }
      }
    },
    searchStore: {
      get: function () {
        return this.$store.getters.search
      },
      set: function (data) {
        this.$store.dispatch('setSearch', data)
      }
    },
    activeTab: {
      get: function () {
        return this.$store.getters.activeTab
      },
      set: function (data) {
        this.$store.dispatch('setActiveTab', data)
      }
    },
    serviceAccountTokens: {
      get: function () {
        return this.$store.getters.serviceAccountTokens
      },
      set: function (data) {
        this.$store.dispatch('setServiceAccountTokens', data)
      }
    },
    sendAsEmails: {
      get: function () {
        return this.$store.getters.sendAsEmails
      },
      set: function (data) {
        this.$store.dispatch('setSendAsEmails', data)
      }
    },
    tagList: {
      get: function () {
        return this.$store.getters.tagList
      },
      set: function (data) {
        this.$store.dispatch('setTagList', data)
      }
    },
    searchDebounceTag () {
      return debounce(this.searchTag, 500)
    },
    setDebounceSearch () {
      return debounce(this.setSearch, 1000)
    }
  },
  methods: {
    setScrollPosition (e) {
      const scrollLeft = e.srcElement.scrollLeft
      this.scrollInStart = !scrollLeft
    },
    setSearch (data) {
      this.searchStore = data
    },
    setTypeSearch (type) {
      this.filterByTag = {}
      this.searchBySelect = type
      if (type === 'name') {
        const search = this.$refs.bbeInputSearch
        if (search) search.value = ''
      }
    },
    inputSearch (e) {
      const val = e.target.value
      const searchInTemplates = this.activeTab === 'TEMPLATES'
      const search = {
        keywordGmail: '',
        keywordTemplates: ''
      }
      if (this.searchByHashtag) {
        this.filterByTag = {}
        this.searchDebounceTag(val)
        return
      } else if (searchInTemplates) {
        search.keywordTemplates = val
      } else {
        search.keywordGmail = val
      }
      this.setDebounceSearch(search)
    },
    searchTag (val) {
      if (!val) {
        this.fullTagList = this.tagList
        return
      }
      const regForFilter = new RegExp(val, 'gmi')
      const result = this.tagList.filter(tag => {
        regForFilter.lastIndex = 0
        return regForFilter.test(tag.name)
      })
      const regForSort = new RegExp('^' + val, 'gmi')
      this.fullTagList = result.sort((a, b) => {
        regForSort.lastIndex = 0
        return regForSort.test(b.name) - regForSort.test(a.name)
      })
    },
    clickToTab (tab) {
      if (tab.id === 'Saved (Business Boutique Group)') return
      if (tab.id === 'To-do (Business Boutique Group)') return
      if (tab.id === 'TEMPLATES') this.activeEmail = {}
      this.activeTab = tab.id
      const tabName = this.activeTab.toLowerCase()
      this.routerPush({name: 'TabOpen', params: {tab: tabName}})
      const search = this.$refs.bbeInputSearch
      if (search) search.value = ''
      this.filterByTag = {}
    },
    setFilterByTag (tag) {
      const input = this.$refs.bbeInputSearch
      if (input) input.value = tag.name
      this.filterByTag = tag
    },
    scrollUp () {
      const mailsList = get(this.$refs, 'bbeMailsList', false)
      const scroll = get(mailsList, '$refs.bbeScrollBar.$el', false)
      if (!scroll) return
      scroll.scrollTop = 0
    },
    resetActiveTab () {
      const tabName = this.activeTab.toLowerCase()
      this.activeTab = sidebarTabs[0].id
      this.routerPush({name: 'TabOpen', params: {tab: tabName}})
    },
    setCounters (onlySystem) {
      const userId = get(this.activeUser, 'gmail.email', 'me')
      if (userId === 'me') return
      const tabs = onlySystem ? filter(sidebarTabs, {type: labelTypes.system}) : sidebarTabs
      tabs.forEach(item => {
        this.getLabels(userId, item.id, item)
      })
    },
    // getLabelsList (userId) {
    //   this.setCounters(true)
    //   this.$gapi.request({
    //     path: `https://www.googleapis.com/gmail/v1/users/${userId}/labels/`,
    //     method: 'GET'
    //   })
    //     .then(resp => {
    //       const labels = get(resp, 'result.labels', [])
    //       const userLabels = filter(labels, {type: labelTypes.user})
    //       const userTabs = filter(sidebarTabs, {type: labelTypes.user})
    //       userTabs.forEach(tab => {
    //         const label = find(userLabels, {name: tab.labelName})
    //         if (label) {
    //           tab.id = label.id
    //           this.getLabels(userId, label.id, tab)
    //         } else {
    //           this.createLabel(userId, tab)
    //         }
    //       })
    //       this.clearLabels()
    //     })
    //     .catch(err => {
    //       console.error(err)
    //     })
    // },
    // createLabel (userId, tab) {
    //   if (!userId || !tab.id) return
    //   this.$gapi.request({
    //     path: `https://www.googleapis.com/gmail/v1/users/${userId}/labels/`,
    //     method: 'POST',
    //     body: {
    //       labelListVisibility: 'labelShow',
    //       messageListVisibility: 'show',
    //       name: tab.id
    //     }
    //   })
    //     .then(resp => {
    //       tab.id = get(resp, 'result.id', '')
    //       this.getLabels(userId, tab.id, tab)
    //     })
    //     .catch(err => {
    //       console.error(err)
    //     })
    // },
    // getLabels (userId, labelId, item) {
    //   if (item.type === labelTypes.withoutGoogle) return
    //   this.$gapi.request({
    //     path: `https://www.googleapis.com/gmail/v1/users/${userId}/labels/${labelId}`,
    //     method: 'GET'
    //   }).then(response => {
    //     const withCounter = labelId === 'INBOX' || item.type === labelTypes.user
    //     item.threadsUnread = withCounter ? response.result.threadsUnread : 0
    //     item.threadsTotal = response.result.threadsTotal
    //   })
    //     .catch(err => {
    //       item.threadsUnread = 0
    //       item.threadsTotal = 0
    //       console.error(err, labelId)
    //     })
    // },
    // addSendAs (userId) {
    //   const sendAsEmail = this.sendAsGmail.email
    //   const displayName = this.sendAsGmail.name
    //   this.createSendAs({userId, sendAsEmail, displayName, tokens: this.serviceAccountTokens})
    //     .then(resp => {
    //       this.getSendAsList(userId)
    //     })
    //     .catch(err => {
    //       if (err.status === 401) this.$gapi.signOut()
    //       console.error(err)
    //     })
    // },
    // getSendAsList (userId) {
    //   this.$gapi.request({
    //     path: `https://www.googleapis.com/gmail/v1/users/${userId}/settings/sendAs`,
    //     method: 'GET'
    //   }).then(resp => {
    //     this.sendAsEmails = get(resp, 'result.sendAs', []).map(item => {
    //       item.name = item.displayName
    //       item.email = item.sendAsEmail
    //       return item
    //     })
    //     const mailIndex = findIndex(this.sendAsEmails, {sendAsEmail: this.sendAsGmail.email})
    //     if (mailIndex === -1) this.addSendAs(userId)
    //   })
    //     .catch(err => {
    //       console.error(err)
    //     })
    // },
    // setServiceTokens (err, tokens) {
    //   if (err) {
    //     console.error(err)
    //     return
    //   }
    //   this.serviceAccountTokens = tokens
    //   const userId = get(this.activeUser, 'gmail.email', 'me')
    //   this.getSendAsList(userId)
    // },
    getTagList () {
      tagsApi.getTags({})
        .then(resp => {
          this.tagList = orderBy(resp.data.tags, [item => item.name.toLowerCase()], ['asc'])
        })
        .catch(err => {
          console.error(err)
        })
    }
  },
  watch: {
    tagList (newVal) {
      this.fullTagList = newVal
    },
    activeUser () {
      // if (!this.userGmail || this.userGmail !== this.userEmail) return
      this.resetActiveTab()
      // this.getLabelsList(this.userGmail)
      // this.authServiceAccount(this.userGmail, this.setServiceTokens)
    },
    activeTab (newTab, oldTab) {
      if (newTab === 'TEMPLATES') this.searchBySelect = 'name'
      if (newTab !== oldTab) {
        this.scrollUp()
      }
    }
  },
  beforeMount () {
    // if (!this.userGmail || this.userGmail !== this.userEmail) return
    this.getTagList()
    // if (this.userGmail) this.authServiceAccount(this.userGmail, this.setServiceTokens)
    // if (this.gmailApi && this.userGmail) this.getLabelsList(this.userGmail)
  }
}
</script>

<style lang="scss" scoped>
@import "../../../sass/variables";
@import "../../../sass/mixins";

.bbe-sidebar {
  display: flex;
  flex-direction: column;
  width: 545px;
  min-width: 545px;
  height: 99.8vh;

  &-header {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    background-color: $color-white;

    &-search {
      position: relative;
      display: flex;
      align-items: center;
      margin: 0 22px 0;
      height: 42px;

      &__icon-wrapper {
        position: absolute;
        left: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &__input {
        padding-left: 41px;
        padding-right: 90px;
      }

      &__icon {
        font-size: 15px;
        color: $color-silver-chalice;

        &_tag {
          font-size: 13px;
        }
      }

      &-select {
        position: absolute;
        display: flex;
        align-items: center;
        right: 14px;
        color: $color-accent;
        transition: 0.3s;
        cursor: pointer;

        &:hover {
          color: $color-accent-hover;
        }

        &__title {
          font-weight: 500;
          font-size: 13px;
          line-height: 13px;
        }

        &__icon {
          font-size: 5px;
          margin-left: 4px;
          transition: 0.3s;

          &_active {
            transform: rotate(180deg);
          }

          &_settings {
            font-size: 14px;
            margin-left: 1px;
          }

        }
      }

      &-options {
        position: absolute;
        top: 20px;
        right: 0;
        display: grid;
        grid-gap: 8px;
        box-shadow: 0 0 15px rgba($color-black, 0.08);
        border-radius: 4px;
        background-color: $color-white;
        padding: 16px 12px;
        z-index: 2;

        &__item {
          font-weight: 500;
          font-size: 13px;
          line-height: 16px;
          color: $color-black;
          transition: 0.3s;

          &:hover {
            color: $color-accent;
          }
        }
      }
    }

    &-tags {
      display: inline-flex;
      margin-top: 8px;
      padding: 0 22px;
      width: 100%;
      height: 24px;
      overflow: hidden;

      &:after,
      &:before {
        content: '';
        position: sticky;
        top: 0;
        height: 24px;
        min-width: 44px;
        z-index: 1;
      }

      &:after {
        right: -23px;
        background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 1) 50%);
      }

      &:before {
        left: -22px;
        background: linear-gradient(90deg, rgba(255, 255, 255, 1) 50%, rgba(0, 0, 0, 0) 100%);
      }

      &_start {
        &:before {
          display: none;
        }
      }

      &__tag {
        padding: 4px;
        margin-right: 8px;
        border-radius: 4px;
        color: $color-accent;
        background-color: $color-link-water;
        font-weight: 500;
        font-size: 13px;
        line-height: 16px;
        white-space: nowrap;
        user-select: none;
        transition: 0.3s;
        cursor: pointer;

        &:hover {
          color: $color-accent-hover;
        }
      }

      &__padding {
        min-width: 14px;
        min-height: 24px;
      }
    }

    &-tabs {
      display: grid;
      grid-template-columns: repeat(auto-fit, 20px);
      justify-content: space-between;
      padding: 0 50px;
      margin-top: 27px;

      &-item {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        padding-bottom: 10px;
        font-size: 20px;
        line-height: 1;
        color: $color-silver-chalice;
        cursor: pointer;
        user-select: none;
        transition: 0.3s;

        &:hover {
          color: $color-dusty-gray;
        }

        &__icon {
          &:before {
            margin: 0;
          }
        }

        &:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: -20px;
          width: calc(100% + 40px);
          min-width: 100%;
          height: 0;
          background-color: $color-accent;
          border-radius: 10px 10px 0 0;
          transition: .2s ease;
        }

        &_disabled {
          pointer-events: none;
        }

        &_active {
          color: $color-accent;
          pointer-events: none;

          &:after {
            height: 3px;
          }
        }

        &__count {
          position: absolute;
          top: -10px;
          left: 6px;
          min-width: 19px;
          margin-left: 7px;
          padding: 3px 7px;
          color: $color-white;
          font-weight: 600;
          font-size: 11px;
          background: $color-accent;
          border: 2px solid $color-white;
          box-sizing: border-box;
          border-radius: 77px;
          text-align: center;
        }

        &__title {
          position: absolute;
          bottom: -21px;
          color: $color-white;
          font-weight: 500;
          font-size: 11px;
          padding: 4px 5px;
          background: rgba($color-black, 0.59);
          border-radius: 3px;
          z-index: 3;
          pointer-events: none;
          user-select: none;
          white-space: nowrap;
        }
      }
    }
  }

  &-content {
    position: relative;
    display: flex;
    flex-direction: column;
    max-height: calc(100% - 118px);
    min-height: calc(100% - 118px);
    height: calc(100% - 118px);
    padding-right: 15px;
    margin-right: -15px;
  }
}

</style>

<style lang="scss">
@import "../../../sass/variables";
@import "../../../sass/mixins";

.bbe-sidebar {
  .ps__rail-x,
  .ps__thumb-x {
    display: none !important;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Main/EmailSidebar/EmailSidebar.vue